/**
 * XP Engine for calculating and managing user experience points
 * This module handles all XP-related operations for the digital discipline features
 */

/**
 * Calculate XP based on task properties
 * @param {Object} params - Parameters for XP calculation
 * @param {string} params.type - Task type: focus, habit, detox, or challenge
 * @param {number} params.durationInMinutes - Duration in minutes
 * @param {string} params.category - Category: productivity, health, learning, or detox
 * @param {string} params.difficulty - Difficulty level: easy, medium, or hard
 * @returns {number} Calculated XP amount
 */
export function calculateXP({ type, durationInMinutes, category, difficulty = 'medium' }) {
  const baseXP = {
    focus: 10,
    habit: 8,
    detox: 12,
    challenge: 15,
  };

  const categoryBonus = {
    productivity: 1.1,
    health: 1.2,
    learning: 1.3,
    digital_detox: 1.4,
    mindfulness: 1.25,
    habit: 1.15
  };

  const difficultyMultiplier = {
    easy: 1,
    medium: 1.5,
    hard: 2,
  };

  // Ensure we have valid values by using defaults for missing parameters
  const safeType = baseXP[type] ? type : 'focus';
  const safeCategory = categoryBonus[category] ? category : 'productivity';
  const safeDifficulty = difficultyMultiplier[difficulty] ? difficulty : 'medium';
  
  const xp = baseXP[safeType] * 
            (durationInMinutes / 10) * // Scale by duration (10 minutes = 1x multiplier)
            categoryBonus[safeCategory] * 
            difficultyMultiplier[safeDifficulty];

  return Math.round(xp);
}

/**
 * Calculate XP penalty for early termination of tasks
 * @param {number} originalXP - Original XP amount
 * @param {number} progressPercentage - Completion percentage (0-100)
 * @returns {number} Adjusted XP after penalty
 */
export function calculateEarlyTerminationPenalty(originalXP, progressPercentage) {
  // If less than 25% complete, no XP
  if (progressPercentage < 25) return 0;
  
  // If less than 50% complete, severe penalty
  if (progressPercentage < 50) return Math.round(originalXP * 0.25);
  
  // If less than 75% complete, moderate penalty
  if (progressPercentage < 75) return Math.round(originalXP * 0.5);
  
  // If more than 75% complete, slight penalty
  return Math.round(originalXP * 0.75);
}

/**
 * Calculate streak bonus based on consecutive completions
 * @param {number} streakDays - Number of consecutive days with completed tasks
 * @param {number} baseXP - Base XP amount
 * @returns {number} Bonus XP amount
 */
export function calculateStreakBonus(streakDays, baseXP) {
  if (streakDays < 3) return 0;
  
  // 10% bonus per day after 3 days, capped at 50%
  const bonusMultiplier = Math.min(0.5, (streakDays - 2) * 0.1);
  return Math.round(baseXP * bonusMultiplier);
}

/**
 * Calculate level based on total XP
 * @param {number} totalXP - Total accumulated XP
 * @returns {Object} Level information
 */
export function calculateLevel(totalXP) {
  // XP required for each level follows a quadratic progression
  // Level 1: 0 XP
  // Level 2: 100 XP
  // Level 3: 300 XP
  // Level 4: 600 XP
  // Level 5: 1000 XP
  // and so on...
  
  let level = 1;
  let xpForNextLevel = 100;
  let accumulatedXP = 0;
  
  while (totalXP >= accumulatedXP + xpForNextLevel) {
    accumulatedXP += xpForNextLevel;
    level++;
    xpForNextLevel = level * 100;
  }
  
  const currentLevelXP = accumulatedXP;
  const nextLevelXP = accumulatedXP + xpForNextLevel;
  const progress = ((totalXP - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;
  
  return {
    level,
    currentXP: totalXP,
    nextLevelXP,
    currentLevelXP,
    progress: Math.round(progress),
  };
}

/**
 * Generate user XP summary for display in statistics
 * @param {Array} completedTasks - Array of completed tasks
 * @param {number} totalXP - User's total XP
 * @returns {Object} XP summary data
 */
export function generateXPSummary(completedTasks, totalXP) {
  const levelInfo = calculateLevel(totalXP);
  
  // Group XP by category
  const categoryXP = completedTasks.reduce((acc, task) => {
    const category = task.category || 'other';
    if (!acc[category]) acc[category] = 0;
    acc[category] += task.xpReward || 0;
    return acc;
  }, {});
  
  // Calculate XP gained in the last 7 days
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  
  const recentXP = completedTasks
    .filter(task => new Date(task.completedAt) >= sevenDaysAgo)
    .reduce((sum, task) => sum + (task.xpReward || 0), 0);
  
  return {
    level: levelInfo.level,
    totalXP,
    nextLevelXP: levelInfo.nextLevelXP,
    levelProgress: levelInfo.progress,
    categoryDistribution: categoryXP,
    weeklyXP: recentXP,
  };
}

/**
 * Store XP change in user's history
 * @param {string} userId - User ID
 * @param {number} xp - XP amount
 * @param {string} source - Source of XP (task ID, action type, etc.)
 * @param {string} description - Description of the XP gain/loss
 * @returns {Promise} Promise that resolves when XP is recorded
 */
export async function recordXPChange(userId, xp, source, description) {
  try {
    // Here we'd typically make an API call to record the XP change
    // For now, we'll just store it in localStorage as a temporary solution
    const xpHistory = JSON.parse(localStorage.getItem(`xpHistory_${userId}`) || '[]');
    
    xpHistory.push({
      timestamp: new Date().toISOString(),
      xp,
      source,
      description,
    });
    
    localStorage.setItem(`xpHistory_${userId}`, JSON.stringify(xpHistory));
    return true;
  } catch (error) {
    console.error('Failed to record XP change:', error);
    return false;
  }
}

/**
 * Manage XP for user
 * @param {string} userId - User ID
 * @param {number} xp - XP amount (positive for gain, negative for loss)
 * @param {string} source - Source of XP change
 * @param {string} description - Description of the XP change
 * @returns {Promise<Object>} Updated user XP information
 */
export async function updateUserXP(userId, xp, source, description) {
  try {
    // Get current user data
    const userData = JSON.parse(localStorage.getItem(`userData_${userId}`) || '{"totalXP": 0}');
    
    // Update XP
    userData.totalXP = (userData.totalXP || 0) + xp;
    if (userData.totalXP < 0) userData.totalXP = 0; // Prevent negative XP
    
    // Calculate new level
    const levelInfo = calculateLevel(userData.totalXP);
    userData.level = levelInfo.level;
    userData.levelProgress = levelInfo.progress;
    
    // Save updated user data
    localStorage.setItem(`userData_${userId}`, JSON.stringify(userData));
    
    // Record the XP change
    await recordXPChange(userId, xp, source, description);
    
    return {
      totalXP: userData.totalXP,
      level: userData.level,
      levelProgress: userData.levelProgress,
    };
  } catch (error) {
    console.error('Failed to update user XP:', error);
    return null;
  }
}
